import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import EarlyAccessButton from "@/components/EarlyAccessButton";

const NAV = [
  { label: "Underbloom", to: "/underbloom", testid: "nav-underbloom" },
  { label: "Fragrance", to: "/fragrance", testid: "nav-fragrance" },
  { label: "Story", to: "/story", testid: "nav-story" },
  { label: "Journal", to: "/journal", testid: "nav-journal" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || location.pathname !== "/";

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-colors duration-500 ${
        solid ? "bg-ivory border-b border-sage" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-[68px] md:h-[80px] grid grid-cols-3 items-center">
        {/* Left nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className="text-[12px] tracking-[0.22em] uppercase link-underline"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        {/* Mobile burger */}
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden justify-self-start w-8 h-8 flex flex-col justify-center gap-1.5"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          <span className={`block h-px bg-ink transition-all ${open ? "translate-y-[6px] rotate-45" : ""}`} style={{ width: 22 }} />
          <span className={`block h-px bg-ink transition-all ${open ? "opacity-0" : ""}`} style={{ width: 22 }} />
          <span className={`block h-px bg-ink transition-all ${open ? "-translate-y-[6px] -rotate-45" : ""}`} style={{ width: 22 }} />
        </button>

        {/* Center wordmark */}
        <Link
          to="/"
          onClick={goHome}
          data-testid="brand-wordmark"
          className="font-display justify-self-center text-[26px] md:text-[32px] leading-none uppercase"
          style={{ fontWeight: 400, letterSpacing: "0.24em" }}
        >
          VEYLLE
        </Link>

        {/* Right CTA — desktop only */}
        <div className="justify-self-end hidden md:block">
          <EarlyAccessButton
            data-testid="header-cta"
            label="Request early access"
            compact
          />
        </div>
        <div className="justify-self-end md:hidden w-8" aria-hidden></div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-nav-panel" className="md:hidden bg-ivory border-t border-sage">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`m-${n.testid}`}
                className="font-display text-3xl"
              >
                {n.label}
              </Link>
            ))}
            <div className="pt-4">
              <EarlyAccessButton label="Request early access" data-testid="mobile-cta" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
